import {
  ConfigSchema,
  SiteDetailsSchema,
  type Config,
} from "@stackje/common/config";
import {
  SiteInsertPayloadSchema,
  SiteUpdatePayloadSchema,
  type Site,
} from "@/types/site.types";
import { ApiClient } from "@/lib/apiClient";

export class SitesRepository {
  static async insert(siteData: unknown) {
    // Accept unknown to validate first
    const validationResult = SiteInsertPayloadSchema.safeParse(siteData);

    if (!validationResult.success) {
      // Combine all error messages into a single string
      const errorMessage = validationResult.error.errors
        .map((e) => e.message)
        .join("\n");
      return {
        data: null,
        error: {
          message: errorMessage,
          details: validationResult.error.format(),
        },
      };
    }

    const validSiteData = validationResult.data;
    
    return ApiClient.insertSite(validSiteData);
  }

  static async getByUserId(): Promise<{ data: Site; error: null } | { data: null; error: { message: string } }> {
    const result = await ApiClient.getSiteByUserId();
    
    if (result.error) {
      return { data: null, error: result.error };
    }
    
    if (result.data) {
      try {
        let config: Config | null = null;
        if (result.data.config) {
          config = ConfigSchema.parse(result.data.config);
        }
        const parsedSiteDetails = SiteDetailsSchema.parse(
          result.data.siteDetails
        );
        const updatedData: Site = {
          id: result.data.id,
          subdomain: result.data.subdomain,
          userId: result.data.userId,
          config,
          siteDetails: parsedSiteDetails,
          customDomain: result.data.customDomain,
          customDomainValidation: result.data.customDomainValidation,
          cloudflareHostnameId: result.data.cloudflareHostnameId,
          lastModifiedAt: result.data.lastModifiedAt ?? null,
          lastPublishedAt: result.data.lastPublishedAt ?? null,
        };
        return { data: updatedData, error: null };
      } catch (e) {
        console.error("Failed to parse site config:", e);
        return {
          data: null,
          error: { message: "Failed to parse site configuration." },
        };
      }
    }

    return { data: null, error: { message: "No site found." } };
  }

  static async update(id: string, _userId: string, siteData: unknown) {
    if (!id) {
      return { data: null, error: { message: "Site ID cannot be empty." } };
    }
    // userId parameter is kept for compatibility but ignored since auth determines user

    const validationResult = SiteUpdatePayloadSchema.safeParse(siteData);

    if (!validationResult.success) {
      const errorMessage = validationResult.error.errors
        .map((e) => e.message)
        .join("\n");
      return {
        data: null,
        error: {
          message: errorMessage,
          details: validationResult.error.format(),
        },
      };
    }

    const validSiteData = validationResult.data;
    return ApiClient.updateSite(id, validSiteData);
  }
}
